/**
 * Style des lignes à libellé d'un tableau variable, `tableConfig.rowStyles` :
 * gras, italique, souligné, alignement, couleurs du texte, du fond et des
 * bordures. Une règle vise un type de ligne (`{ kind }` : les zones, les
 * lignes vides) ou un libellé (`{ label }`), comme une règle de fusion
 * (cf. resolveLabelRowMerge) : les clés de lignes changent à chaque
 * rafraîchissement, le type et le libellé non. Rangé dans la config, le style
 * survit donc au rafraîchissement, qui remplace les lignes.
 *
 * Règle : `{ kind | label, bold, italic, underline, align, fontFamily, fontSize,
 * color, background, borderColor, hiddenBorders }` ; un réglage absent laisse le
 * style du tableau. `fontSize` : en px, comme la taille des cases du style du
 * tableau.
 * `hiddenBorders` : côtés extérieurs de la ligne sans trait (cf. ROW_BORDER_SIDES).
 * `cells` : `{ [libellé d'origine de la colonne]: { bold, italic, underline,
 * align, fontFamily, fontSize, color, background } }`, réglages d'une seule
 * colonne de la ligne, qui passent avant ceux de la ligne (cf. cellStyleEntry).
 * Les traits n'y sont pas : un trait est partagé entre deux cases.
 *
 * Module partagé document ↔ site : pur, sans React ni store.
 */

import { getLabelRowMergeState, mergeRuleKey } from './tableStructure';

/** Côtés extérieurs d'une ligne dont on peut retirer le trait. */
export const ROW_BORDER_SIDES = ['top', 'bottom', 'left', 'right'];

const identityOf = ({ label, kind }) => (kind ? { kind } : { label });

const isBlank = (value) => value == null || String(value).trim() === '';
const plainText = (value) => (value == null ? '' : String(value)).trim();

/**
 * Ligne que vise le style, avec son état de fusion (cf.
 * getLabelRowMergeState), ou null. S'y ajoutent, avec une structure
 * (`options.structure`, cf. getTableRowStructure), les lignes qui se stylent
 * sans pouvoir fusionner (`styleOnly`), leurs cases étant remplies : une ligne
 * au libellé fixe (« Total forfaitaire ») ou typée (le titre d'une tenue, sa
 * photo à côté).
 */
export const getRowStyleState = (row, columns, isEmptyValue, options) => {
  const state = getLabelRowMergeState(row, columns, isEmptyValue, options);
  if (state || !row || row.manual || !options?.structure || !Array.isArray(columns)) return state;
  const isEmpty = isEmptyValue ?? isBlank;
  const first = columns.find((col) => !isEmpty(row[col.key]));
  const label = first ? (options.getText ?? plainText)(row[first.key]) : '';
  if (!label) return null;
  if (!row.rowKind && !options.structure.fixedLabels.includes(label)) return null;
  return {
    label,
    ...(row.rowKind ? { kind: row.rowKind } : {}),
    merged: false,
    defaultMerged: false,
    styleOnly: true,
  };
};

/**
 * Règle de la ligne `state` (cf. getRowStyleState), ou null. Celle de son
 * type passe avant celle de son libellé, comme pour la fusion.
 */
export const findRowStyle = (rowStyles, state) => {
  if (!state) return null;
  const rules = rowStyles ?? [];
  const byKind = state.kind ? rules.find((rule) => rule.kind === state.kind) : undefined;
  const byLabel = state.label ? rules.find((rule) => !rule.kind && rule.label === state.label) : undefined;
  return byKind ?? byLabel ?? null;
};

/**
 * Règle de chaque ligne de `data` qui en a une, par clé de ligne. Recalculé au
 * rendu comme la fusion auto (cf. computeLabelRowMerges) : suit les nouvelles
 * clés après un rafraîchissement. Une ligne de données sans libellé fixe, ou
 * ajoutée à la main, n'en a jamais. `options` : `rowStyles`, `getText` (texte
 * nu) et `structure` (cf. getRowStyleState).
 */
export const computeRowStyles = (data, columns, isEmptyValue, options) => {
  const byKey = new Map();
  if (!options?.rowStyles?.length || !Array.isArray(data)) return byKey;
  data.forEach((row) => {
    const state = getRowStyleState(row, columns, isEmptyValue, {
      getText: options.getText,
      structure: options.structure,
    });
    const rule = findRowStyle(options.rowStyles, state);
    if (rule) byKey.set(row.key, rule);
  });
  return byKey;
};

const isSet = (value) =>
  value != null && value !== false && value !== '' && !(Array.isArray(value) && !value.length);

// Clé d'une colonne dans `cells` : son libellé d'origine, comme pour le reste de
// la config (« é » d'un caractère ou « e » + accent : une seule clé).
const columnKey = (label) => String(label).normalize('NFC');

/** Réglages propres à la colonne `label` d'une règle, ou null. */
export const cellStyleEntry = (rule, label) =>
  (label != null && rule?.cells?.[columnKey(label)]) || null;

// `cells` après `patch` sur la colonne `label` : un réglage vide revient à celui
// de la ligne, une colonne sans réglage disparaît, comme `cells` sans colonne.
const patchedCells = (cells, label, patch) => {
  const key = columnKey(label);
  // Gras, italique et souligné peuvent y valoir `false` : retirer d'une colonne
  // ce que la ligne met partout. Un réglage vidé (null) revient à la ligne.
  const entry = Object.fromEntries(
    Object.entries({ ...cells?.[key], ...patch }).filter(([, value]) => isSet(value) || typeof value === 'boolean')
  );
  const next = { ...cells };
  if (Object.keys(entry).length) next[key] = entry;
  else delete next[key];
  return Object.keys(next).length ? next : null;
};

// `cells` sans les `fields` réglés sur la ligne entière : le dernier geste gagne,
// la couleur posée sur la ligne n'est pas masquée par celle d'une colonne. Les
// autres réglages des colonnes restent.
const withoutFields = (cells, fields) => {
  const next = {};
  Object.entries(cells).forEach(([key, entry]) => {
    const kept = Object.fromEntries(Object.entries(entry).filter(([field]) => !fields.includes(field)));
    if (Object.keys(kept).length) next[key] = kept;
  });
  return Object.keys(next).length ? next : null;
};

/**
 * Règles après réglage de la ligne `state` : `patch` change des réglages de sa
 * règle, et un réglage vide (null, false, '') revient au style du tableau. Une
 * règle sans réglage disparaît. Avec `columnLabel`, `patch` ne change que les
 * réglages de cette colonne de la ligne (cf. cellStyleEntry) ; sans, il prend la
 * place du même réglage dans les colonnes.
 */
export const setRowStyle = (rowStyles, state, patch, columnLabel = null) => {
  const identity = identityOf(state);
  const key = mergeRuleKey(identity);
  const current = (rowStyles ?? []).find((rule) => mergeRuleKey(rule) === key);
  const rest = (rowStyles ?? []).filter((rule) => mergeRuleKey(rule) !== key);
  const settings = patchRowStyle(current, patch, columnLabel);
  return settings ? [...rest, { ...identity, ...settings }] : rest;
};

/**
 * Réglages d'une seule ligne après `patch`, sans son identité, ou null s'il
 * n'en reste aucun. Même règle que setRowStyle, pour un style rangé sur la
 * ligne elle-même (tableau custom, cf. Table) plutôt que dans une liste.
 */
export const patchRowStyle = (rule, patch, columnLabel = null) => {
  const change = columnLabel == null
    ? { ...patch, ...(rule?.cells && { cells: withoutFields(rule.cells, Object.keys(patch)) }) }
    : { cells: patchedCells(rule?.cells, columnLabel, patch) };
  const settings = Object.entries({ ...rule, ...change }).filter(
    ([field, value]) => field !== 'kind' && field !== 'label' && isSet(value)
  );
  return settings.length ? Object.fromEntries(settings) : null;
};

/**
 * Style d'une seule ligne sans ses réglages (null), ou sans ceux de la colonne
 * `columnLabel`. Pendant de resetRowStyle, comme patchRowStyle de setRowStyle.
 */
export const clearRowStyle = (rule, columnLabel = null) => {
  if (columnLabel == null) return null;
  const entry = cellStyleEntry(rule, columnLabel);
  if (!entry) return rule ?? null;
  return patchRowStyle(rule, Object.fromEntries(Object.keys(entry).map((field) => [field, null])), columnLabel);
};

/**
 * Style CSS des cases d'une ligne réglée (`isFirstColumn` / `isLastColumn` : la
 * case porte le bord gauche / droit de la ligne, seuls à pouvoir être retirés), par-dessus celui du tableau, ou null.
 * L'alignement n'y est pas : chaque rendu le pose à sa façon.
 * Bordures fusionnées (border-collapse) : le trait entre deux lignes est celui
 * de la ligne du dessus. La couleur de bordure de la ligne suivante
 * (`nextRule`) passe donc sur le bas de celle-ci, sinon son haut garderait la
 * couleur du tableau.
 */
export const rowStyleCss = (rule, nextRule = null, { isFirstColumn = true, isLastColumn = true } = {}) => {
  // `hidden` l'emporte, en bordures fusionnées, sur tout autre trait de la même
  // arête : le trait partagé avec la ligne voisine disparaît des deux côtés.
  const hiddenSides = rule?.hiddenBorders ?? [];
  const hides = (side) => hiddenSides.includes(side);
  const css = {
    ...(rule?.bold != null && { fontWeight: rule.bold ? 'bold' : 'normal' }),
    ...(rule?.italic != null && { fontStyle: rule.italic ? 'italic' : 'normal' }),
    ...(rule?.underline != null && { textDecoration: rule.underline ? 'underline' : 'none' }),
    ...(rule?.fontFamily && { fontFamily: rule.fontFamily }),
    ...(rule?.fontSize && { fontSize: rule.fontSize }),
    ...(rule?.color && { color: rule.color }),
    ...(rule?.background && { backgroundColor: rule.background }),
    ...(rule?.borderColor && { borderColor: rule.borderColor }),
    ...(!rule?.borderColor && nextRule?.borderColor && { borderBottomColor: nextRule.borderColor }),
    ...(hides('top') && { borderTopStyle: 'hidden' }),
    ...(hides('bottom') && { borderBottomStyle: 'hidden' }),
    ...(hides('left') && isFirstColumn && { borderLeftStyle: 'hidden' }),
    ...(hides('right') && isLastColumn && { borderRightStyle: 'hidden' }),
  };
  // Rien à poser : null, pas un objet neuf qui ferait se redessiner les cases
  // mémoïsées du document.
  return Object.keys(css).length ? css : null;
};

/**
 * Règles sans celle de la ligne `state` : elle reprend le style du tableau.
 * Avec `columnLabel`, seule cette colonne de la ligne perd ses réglages.
 */
export const resetRowStyle = (rowStyles, state, columnLabel = null) => {
  const identity = identityOf(state);
  const key = mergeRuleKey(identity);
  const current = (rowStyles ?? []).find((rule) => mergeRuleKey(rule) === key);
  const rest = (rowStyles ?? []).filter((rule) => mergeRuleKey(rule) !== key);
  if (columnLabel != null && !cellStyleEntry(current, columnLabel)) return rowStyles ?? [];
  const settings = clearRowStyle(current, columnLabel);
  return settings ? [...rest, { ...identity, ...settings }] : rest;
};
